import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { PROTECTED_ROUTES } from "@/constants/routes";
import { WizardActionBar } from "@/features/listings/components/WizardActionBar";
import { useSellWizard } from "@/features/listings/context/SellWizardContext";
import {
  detailsStepSchema,
  type DetailsStepValues,
} from "@/features/listings/schemas/listing.schemas";
import { useCategories } from "@/features/marketplace/hooks/useCategories";
import type { ListingSpec } from "@/features/listings/types/listing";

const CONDITIONS: Array<{ value: DetailsStepValues["condition"]; label: string }> = [
  { value: "new", label: "New" },
  { value: "like-new", label: "Like New" },
  { value: "used", label: "Used" },
  { value: "for-parts", label: "For Parts" },
];

/** docs/ui/routes.md `/sell/details` — Create Listing Step 2. */
export function SellDetailsPage() {
  const navigate = useNavigate();
  const { draft, updateDraft } = useSellWizard();
  const categoriesQuery = useCategories();
  const [specs, setSpecs] = useState<ListingSpec[]>(draft.specs);

  // Reachable directly by URL, skipping Step 1 — send the seller back to
  // add photos first rather than let them continue with no images.
  useEffect(() => {
    if (draft.images.length === 0) {
      navigate(PROTECTED_ROUTES.sellMedia, { replace: true });
    }
  }, [draft.images.length, navigate]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DetailsStepValues>({
    resolver: zodResolver(detailsStepSchema),
    defaultValues: {
      title: draft.title,
      description: draft.description,
      categorySlug: draft.categorySlug,
      condition: draft.condition || undefined,
      brand: draft.brand,
    },
  });

  function addSpec() {
    setSpecs((prev) => [...prev, { label: "", value: "" }]);
  }

  function updateSpec(index: number, patch: Partial<ListingSpec>) {
    setSpecs((prev) => prev.map((spec, i) => (i === index ? { ...spec, ...patch } : spec)));
  }

  function removeSpec(index: number) {
    setSpecs((prev) => prev.filter((_, i) => i !== index));
  }

  function onSubmit(values: DetailsStepValues) {
    const cleanSpecs = specs.filter((spec) => spec.label.trim() && spec.value.trim());
    updateDraft({ ...values, brand: values.brand ?? "", specs: cleanSpecs });
    navigate(PROTECTED_ROUTES.sellFinalize);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-h2 text-foreground">Describe your item</h1>
        <p className="text-body text-muted-foreground">
          A clear title and description help buyers find and trust your listing.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="title">Title</Label>
        <Input id="title" {...register("title")} />
        {errors.title && (
          <p className="text-caption text-destructive" role="alert">
            {errors.title.message}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="description">Description</Label>
        <Textarea id="description" rows={5} {...register("description")} />
        {errors.description && (
          <p className="text-caption text-destructive" role="alert">
            {errors.description.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="categorySlug">Category</Label>
          <Controller
            name="categorySlug"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="categorySlug">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesQuery.data?.map((category) => (
                    <SelectItem key={category.id} value={category.slug}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.categorySlug && (
            <p className="text-caption text-destructive" role="alert">
              {errors.categorySlug.message}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="condition">Condition</Label>
          <Controller
            name="condition"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="condition">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((condition) => (
                    <SelectItem key={condition.value} value={condition.value}>
                      {condition.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.condition && (
            <p className="text-caption text-destructive" role="alert">
              {errors.condition.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="brand">Brand (optional)</Label>
        <Input id="brand" {...register("brand")} />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Specifications (optional)</Label>
          <Button type="button" variant="ghost" size="sm" onClick={addSpec}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add spec
          </Button>
        </div>
        {specs.map((spec, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              aria-label="Spec label"
              placeholder="e.g. Storage"
              value={spec.label}
              onChange={(event) => updateSpec(index, { label: event.target.value })}
            />
            <Input
              aria-label="Spec value"
              placeholder="e.g. 128GB"
              value={spec.value}
              onChange={(event) => updateSpec(index, { value: event.target.value })}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove spec"
              onClick={() => removeSpec(index)}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
      </div>

      <WizardActionBar
        onBack={() => navigate(PROTECTED_ROUTES.sellMedia)}
        primaryLabel="Continue"
        onPrimary={handleSubmit(onSubmit)}
      />
    </form>
  );
}
